import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAccount } from '@/entities/session'
import { onboardingSkipped, useSetup } from '@/entities/setup'
import { useBotStore } from '@/entities/bot'
import styles from './guards.module.css'

/**
 * Пускает дальше только вошедших.
 *
 * Пока идёт проверка — заглушка на весь экран, а не форма входа.
 * Показать форму и через мгновение убрать её значит мигнуть страницей
 * входа человеку, который уже вошёл; выглядит как сбой.
 */
export function RequireAuth() {
  const { data, isPending } = useAccount()
  const location = useLocation()

  if (isPending) return <FullScreenLoader />

  if (!data) {
    // Запоминаем, куда шли: после входа вернём туда же, а не на панель.
    // Ссылку на конкретный раздел присылают в переписке, и терять её на
    // каждом входе — мелкая, но ежедневная неприятность.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}

/** Не раскрывает существование админки обычному пользователю: он видит обычную 404. */
export function RequireAdmin() {
  const { data, isPending } = useAccount()

  if (isPending) return <FullScreenLoader />
  if (data?.impersonation) return <Navigate to="/" replace />
  if (!data?.isSuperadmin) return <Navigate to="/not-found" replace />

  return <Outlet />
}

/**
 * Пускает в кабинет только с настроенным агентом.
 *
 * Ненастроенный кабинет — это обзор с прочерками и агент, который на
 * звонке не знает, что продаёт. Поэтому после регистрации человек
 * оказывается в онбординге не по ссылке, а потому что кабинета для него
 * пока не существует. «Настроить позже» этот замок снимает — запирать
 * пришедшего осмотреться нельзя.
 *
 * Ошибка запроса пропускает внутрь: лучше кабинет с прочерками, чем
 * человек, запертый снаружи из-за сетевого сбоя.
 */
export function RequireSetup() {
  const setup = useSetup()
  // Готовность кабинета — это готовность основного бота. Выбран другой —
  // пускаем: иначе смена бота на секунду гасила бы весь кабинет загрузкой,
  // а недописанный черновик бота уводил бы в онбординг.
  const botSelected = useBotStore((s) => s.botId !== null)
  if (botSelected) return <Outlet />

  if (setup.isPending) return <FullScreenLoader />
  if (setup.data && !setup.data.ready && !onboardingSkipped()) {
    return <Navigate to="/onboarding" replace />
  }

  return <Outlet />
}

/** Вошедшему нечего делать на странице входа. */
export function RequireGuest() {
  const { data, isPending } = useAccount()

  if (isPending) return <FullScreenLoader />
  if (data) return <Navigate to="/" replace />

  return <Outlet />
}

function FullScreenLoader() {
  return (
    <div className={styles.loader}>
      <span className={styles.spinner} />
    </div>
  )
}
